import * as Sentry from '@sentry/react';
import {
    tooManyRequestsStatusCodes,
    unauthorisedStatusCodes,
    validationErrorStatusCodes,
} from '_constants/statusCodes';
import { version } from '_currentVersion';

const envType = process.env.REACT_APP_ENVIRONMENT ?? undefined;

// List of module names or filename parts to ignore in Sentry reports
const ignoredModules = ['react-sortable-hoc'];

const allowedUrls = [
    'app.knitandnote.com',
    'stage.knitandnote.com',
    'market.knitandnote.com',
    'market.stage.knitandnote.com',
    'backend.app.knitandnote.com',
    'backend.stage.knitandnote.com',
];
const deniedUrls = [
    'extensions/',
    'chrome-extension://',
    'safari-extension://',
];

// 404s from these endpoint paths are expected and should be ignored
const ignored404Paths = ['login-otp'];

export const sentryUrl = process.env.REACT_APP_SENTRY_DNS ?? null;

const getClientMeta = () => {
    if (typeof window === 'undefined') return undefined;
    const nav = navigator as Navigator &
        Partial<{
            connection: { type?: string; downlink?: number; rtt?: number };
            deviceMemory: number;
        }>;
    return {
        online: nav.onLine,
        userAgent: nav.userAgent,
        connectionType: nav.connection?.type,
        connectionDownlink: nav.connection?.downlink,
        connectionRtt: nav.connection?.rtt,
        deviceMemory: (nav as { deviceMemory?: number }).deviceMemory,
        hwConcurrency: nav.hardwareConcurrency,
        visibilityState: document?.visibilityState,
    };
};

const SentryService = {
    init: (): void => {
        if (!sentryUrl) {
            return;
        }
        const appVersion = version
            ? 'kan-reactjs-pwa@' + version
            : 'no-version';
        const captureConsoleLevels = ['error', 'warn'];

        Sentry.init({
            dsn: sentryUrl,
            integrations: [
                // Performance tracing
                Sentry.browserTracingIntegration({}),
                // Rich, automatic breadcrumbs (XHR/fetch, history/navigation, DOM, etc.)
                // Note: we disable console here to avoid duplicate console breadcrumbs
                // since we use captureConsoleIntegration below with level filtering.
                Sentry.breadcrumbsIntegration({
                    console: false,
                    dom: true,
                    fetch: true,
                    history: true,
                    xhr: true,
                }),
                Sentry.replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false,
                }),
                // Android users get more verbose console capture to help debug WebView issues
                Sentry.captureConsoleIntegration({
                    levels: captureConsoleLevels,
                }),
            ],
            environment: envType,
            normalizeDepth: 10, //Deep for redux state
            release: appVersion,
            tracesSampleRate: 0.1, // Capture 10% of the transactions
            // Session Replay
            replaysSessionSampleRate: 0.02, // This sets the sample rate at 2%
            replaysOnErrorSampleRate: 1.0, //  The sample rate of 100% when sampling sessions where errors occur.
            allowUrls: allowedUrls,
            denyUrls: deniedUrls,
            maxBreadcrumbs: 50,
            tracesSampler: (context) => {
                // Keep high sampling for errors; reduce for routine page views
                if (context.parentSampled) return context.parentSampled;
                const txName = context.transactionContext?.name ?? '';
                if (
                    txName.includes('/landing') ||
                    txName.includes('/projects')
                ) {
                    return 0.2;
                }
                return 0.1;
            },
            beforeSend: (event, hint) => {
                const error = hint?.originalException;

                // Attach lightweight client meta once per event for Android/WebView diagnostics
                const clientMeta = getClientMeta();
                if (clientMeta) {
                    event.extra = { ...event.extra, clientMeta };
                }

                // Drop handled client-side abort noise
                const handledFlag =
                    (event?.extra as { isHandled?: boolean } | undefined)
                        ?.isHandled ?? false;
                const abortReason =
                    (event?.tags as { abort_reason?: string } | undefined)
                        ?.abort_reason ??
                    (event?.extra as { abort_reason?: string } | undefined)
                        ?.abort_reason;
                if (handledFlag || abortReason) {
                    return null;
                }

                // Enhanced debugging for React Hook rule violations
                if (
                    event.exception &&
                    event.exception.values &&
                    event.exception.values.length > 0
                ) {
                    const exceptionValue = event.exception.values[0];
                    const errorMessage = exceptionValue.value || '';

                    // Check for React Hook rule violations (error #300, #301, #302, #303, #304, #305, #306, #307, #308)
                    if (errorMessage.includes('Minified React error')) {
                        const errorNumberMatch =
                            errorMessage.match(/React error #(\d+)/);
                        if (errorNumberMatch) {
                            const errorNumber = errorNumberMatch[1];
                            // Add context for React Hook rule violations
                            if (
                                [
                                    '300',
                                    '301',
                                    '302',
                                    '303',
                                    '304',
                                    '305',
                                    '306',
                                    '307',
                                    '308',
                                ].includes(errorNumber)
                            ) {
                                event.tags = {
                                    ...event.tags,
                                    react_error_type: 'hook_violation',
                                    react_error_number: errorNumber,
                                    debugging_hint:
                                        'Check for conditional hook calls or inconsistent hook execution order',
                                };

                                event.extra = {
                                    ...event.extra,
                                    react_debug_info: {
                                        error_type: 'React Hook Rule Violation',
                                        common_causes: [
                                            'Conditional hook calls (useEffect, useState, etc. inside if statements)',
                                            'Hooks called in loops or nested functions',
                                            'Early returns before hook calls',
                                            'Different number of hooks rendered between renders',
                                        ],
                                        debugging_steps: [
                                            'Check all hook calls are at the top level of the component',
                                            'Ensure no hooks are called conditionally',
                                            'Verify hook execution order is consistent across renders',
                                            'Look for early returns that skip hook calls',
                                        ],
                                    },
                                };
                            }
                        }
                    }
                }

                if (
                    event.exception &&
                    event.exception.values &&
                    event.exception.values.length > 0 && // Check if values array is not empty
                    event.exception.values[0].stacktrace &&
                    event.exception.values[0].stacktrace.frames
                ) {
                    const frames = event.exception.values[0].stacktrace.frames;
                    const foundIgnoredModule = frames.some((frame) => {
                        const filename = frame.filename ?? '';
                        const moduleName = frame.module ?? '';
                        return ignoredModules.some(
                            (ignored) =>
                                filename.includes(ignored) ||
                                moduleName.includes(ignored),
                        );
                    });

                    if (foundIgnoredModule) {
                        return null; // Don't send the event
                    }
                }

                // Check if the error is from GTM
                const errorStack =
                    (hint?.originalException as Error)?.stack ?? '';
                if (
                    typeof errorStack === 'string' &&
                    errorStack.includes('/gtm.js')
                ) {
                    return null;
                }

                // Check if this is an Axios Network Error
                if (
                    error &&
                    typeof error === 'object' &&
                    'message' in error &&
                    error.message === 'Network Error' &&
                    'code' in error &&
                    (error.code === 'ERR_NETWORK' ||
                        error.code === 'ECONNABORTED')
                ) {
                    return null;
                }

                // Suppress expected 404 noise for whitelisted endpoints
                if (error && typeof error === 'object') {
                    const response = (
                        error as {
                            response?: {
                                status?: number | string;
                                config?: { url?: string };
                            };
                        }
                    ).response;
                    const statusCode = response?.status;
                    const requestUrl =
                        response?.config?.url ??
                        (error as { config?: { url?: string } }).config?.url ??
                        '';

                    const isIgnored404 =
                        (statusCode === 404 || statusCode === '404') &&
                        typeof requestUrl === 'string' &&
                        ignored404Paths.some((path) =>
                            requestUrl.includes(path),
                        );

                    if (isIgnored404) {
                        return null;
                    }
                }

                // Check if it's an Axios error with a response status that we want to filter
                if (error && typeof error === 'object' && 'response' in error) {
                    const response = error.response;
                    // Make sure response is an object with a status property
                    if (
                        response &&
                        typeof response === 'object' &&
                        'status' in response
                    ) {
                        const statusCode = response.status as number | string;

                        // Filter out 401, 422, and 429 status codes
                        if (
                            unauthorisedStatusCodes.includes(statusCode) ||
                            validationErrorStatusCodes.includes(statusCode) ||
                            tooManyRequestsStatusCodes.includes(statusCode)
                        ) {
                            // Return null to prevent the event from being sent to Sentry
                            return null;
                        }
                    }
                }

                // Return the event to allow it to be sent to Sentry
                return event;
            },
        });
    },
};

export default SentryService;
